import { alpha, styled, Typography } from '@mui/material';
import { UploadFileResult } from 'hooks/ui/useFileUpload';
import Button from 'components/Button';
import api from 'lib/trpcClient';
import { useLayoutContext } from 'contexts/LayoutContext';
import InteractiveTable from 'components/InteractiveTable';
import { usePagination } from 'further-ui/hooks';
import { numberToCurrencyString, pluralize } from 'further-ui/utils';
import {
  CheckCircleOutlineOutlined,
  ErrorOutlineOutlined,
} from '@mui/icons-material';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { Column, Row } from 'further-ui/components/Layout';
import { getQueryKey } from '@trpc/react-query';
import {
  BANK_RECONCILIATION,
  BankReconciliationTab,
} from 'adminConstants/routes';

const Table = styled(InteractiveTable)(({ theme }) => ({
  ['& .statement-entry-with-error']: {
    backgroundColor: alpha(theme.palette.error.main, 0.3),

    ['&:hover']: {
      backgroundColor: alpha(theme.palette.error.main, 0.4),
    },
  },
}));

const columns = [
  {
    field: 'date',
    headerName: 'Date',
    renderCell: ({ row }) => row.date.toLabel(),
    flex: 1,
  },
  {
    field: 'counterparty',
    headerName: 'Counterparty',
    flex: 1,
  },
  {
    field: 'description',
    headerName: 'Description',
    flex: 1,
  },
  {
    field: 'spent',
    headerName: 'Spent',
    flex: 1,
    renderCell: ({ row }) =>
      numberToCurrencyString(row.spent, {
        fallback: '-',
        currency: row.currency,
      }),
  },
  {
    field: 'received',
    headerName: 'Received',
    flex: 1,
    renderCell: ({ row }) =>
      numberToCurrencyString(row.received, {
        fallback: '-',
        currency: row.currency,
      }),
  },
];

type Props = {
  uploadedFile: UploadFileResult;
  dateFormat: string;
  onPreviousStep: () => void;
  bankAccountId: string;
};

const Review: React.FC<Props> = ({
  uploadedFile,
  dateFormat,
  onPreviousStep,
  bankAccountId,
}) => {
  const { selectedVehicleId } = useLayoutContext();
  const pagination = usePagination({ order: 'asc', orderBy: 'date' });

  const [data] = api.bankStatement.getPreview.useSuspenseQuery({
    bankAccountId,
    vehicleId: selectedVehicleId ?? '',
    dateFormat: dateFormat ?? '',
    bankStatementFilePath: uploadedFile.filePath,
    ...pagination,
  });

  const { operations, totalCount, totalErrors } = data ?? {
    operations: [],
    totalCount: 0,
    totalErrors: 0,
  };

  const importBankStatement = api.bankStatement.import.useMutation(
    useHandleTrpcMutation()({
      successMessage: `${totalCount} statement ${pluralize(
        'line',
        totalCount,
      )} ${pluralize('has', totalCount, 'have')} been successfully imported`,
      successRedirect:
        BANK_RECONCILIATION + BankReconciliationTab.ReconcileTransactions,
      invalidations: [
        getQueryKey(api.bankStatement),
        getQueryKey(api.bankAccount.listBankAccountOperations),
      ],
    }),
  );

  const handleSubmit = () => {
    if (totalErrors || !dateFormat || !operations?.length) {
      return;
    }

    importBankStatement.mutate({
      vehicleId: selectedVehicleId ?? '',
      bankStatementFilePath: uploadedFile.filePath,
      bankAccountId,
      dateFormat,
    });
  };

  const SummaryIcon = totalErrors
    ? ErrorOutlineOutlined
    : CheckCircleOutlineOutlined;
  const summaryText = totalErrors
    ? `${totalErrors} ${pluralize('error', totalErrors)} found`
    : `${totalCount} ${pluralize('transaction', totalCount)} ${
        totalCount === 1 ? 'is' : 'are'
      } ready to import`;
  const iconColor = totalErrors ? 'error' : 'primary';

  return (
    <Column spacing="lg">
      <Column spacing="sm">
        <Typography variant="h4">Review and complete import</Typography>

        <Row centered spacing="xs">
          <SummaryIcon color={iconColor} fontSize="medium" />
          <Typography variant="body1" color="textSecondary">
            {summaryText}
          </Typography>
        </Row>
      </Column>

      <Column spacing="sm">
        <Typography variant="h4">Transaction preview</Typography>
        <Table
          id="bank-statement-preview"
          disableSorting
          disableTableConfiguration
          columns={columns}
          rows={operations}
          rowCount={totalCount}
          pagination={pagination}
          getRowClassName={(params) => {
            return params.row.hasError ? 'statement-entry-with-error' : '';
          }}
        />
      </Column>
      <Row centered spacing="sm" justify="flex-end">
        <Button
          variant="outlined"
          onClick={onPreviousStep}
          disabled={importBankStatement.isPending}
          size="large"
        >
          Back
        </Button>
        <Button
          variant="contained"
          disabled={!dateFormat || !operations?.length || !!totalErrors}
          onClick={handleSubmit}
          loading={importBankStatement.isPending}
          size="large"
        >
          Complete import
        </Button>
      </Row>
    </Column>
  );
};

export default Review;
